import React, { useState, useEffect, useRef } from 'react';
import styles from '@styles/components/QontactPoints/Turnos/QontactPointTurnos.module.css';

import { ReactComponent as CalendarSVG } from '@assets/SVGs/QontactPoint/CalendarSVG.svg';
import { ReactComponent as ArrowSVG } from '@assets/SVGs/QontactPoint/ArrowSVG.svg';
import { ReactComponent as EliminateSVG } from '@assets/SVGs/QontactPoint/xRedSVG.svg';
import { ReactComponent as ColaboradorSVG } from '@assets/SVGs/QontactPoint/ColaboradorGray.svg';
import { ReactComponent as ColaboradorActiveSVG } from '@assets/SVGs/QontactPoint/ColaboradorActive.svg';
import { ReactComponent as EditSVG } from '@assets/SVGs/Colaborador/EditSVG.svg';

import TimeInput from '@components/General/TimeInput';
import { DayPickerPopper } from '@components/General/TablePoppers';
import { ShiftRecipe, WeeklyConfiguration } from '@models/QontactPoint';

interface QPTurnoProps {
	turno: ShiftRecipe;
	colaboradoresID: string[];
	isEditable: boolean;
	setTurno?: any;
	onDetailClick?: (turno: ShiftRecipe) => void;
	isDeleteable?: boolean;
	isItemFromList?: boolean;
	onDelete?: (turno: ShiftRecipe) => void;
}

const QPTurno: React.FC<QPTurnoProps> = ({ turno, setTurno, colaboradoresID, isEditable, onDetailClick, isDeleteable = false , isItemFromList = true, onDelete}) => {
	const [name, setName] = useState<string>(turno.name);
	const [hourFrom, setHourFrom] = useState<string>(turno.hourFrom === '00:00' ? '' : turno.hourFrom);
	const [hourTo, setHourTo] = useState<string>(turno.hourTo === '00:00' ? '' : turno.hourTo);
	const [expected, setExpected] = useState<number>(turno.expected);

	const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];

	const [selectedDays, setSelectedDays] = useState<string[]>(() => {
		if (
			turno.type === 'weekly' &&
			turno.configurationData &&
			turno.configurationData.weekdays
		) {
			return Object.values(turno.configurationData.weekdays).map((dayIndex: any) => dayNames[dayIndex]);
		}
		return [];
	});

	const [isOpen, setIsOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

	const containerRef = useRef<HTMLDivElement>(null);
	const daysBarRef = useRef<HTMLDivElement>(null);
	const anchorElement = useRef<HTMLDivElement>(null);
	const [popperWidth, setPopperWidth] = useState(0);

	useEffect(() => {
		const container = containerRef.current;
		const onWheel = (event) => {
			if (event.deltaY !== 0) {
				event.preventDefault(); // Prevent the default vertical scroll behavior
				if (container) {
					container.scrollLeft += event.deltaY; // Scroll horizontally instead
				}
			}
		};
		if (container) {
			container.addEventListener('wheel', onWheel);
		}

		const updatePopperWidth = () => {
			if (daysBarRef.current) {
				setPopperWidth(daysBarRef.current.offsetWidth);
			}
		};
		// Initial width set
		updatePopperWidth();

		// Update width on window resize
		window.addEventListener('resize', updatePopperWidth);

		// Cleanup the event listener
		return () => {
			if (container) {
				container.removeEventListener('wheel', onWheel);
			}
			window.removeEventListener('resize', updatePopperWidth);
		};
	}, []);

	useEffect(() => {
		//Update shift recipes in parent component
		if (isEditable && setTurno) {
			setTurno((prev) => {
				if (prev) {
					return {
						...prev,
						name,
						hourFrom,
						hourTo,
						expected,
						configurationData: {
							weekdays: selectedDays.map((day) => dayNames.indexOf(day)).sort(),
						},
					};
				}
				return null;
			});
		}
	}, [name, hourFrom, hourTo, expected, selectedDays]);

	const isInitialRender = useRef(true);

	useEffect(() => {
	  if (isInitialRender.current) {
		// On initial render, only update if colaboradoresID is non-empty
		if (colaboradoresID.length > 0) {
		  setExpected(colaboradoresID.length);
		}
		isInitialRender.current = false;
	  } else {
		// On subsequent renders, always update when colaboradoresID changes
		setExpected(colaboradoresID.length);
	  }
	}, [colaboradoresID]);


	const handleDayRemove = (day: string) => {
		setSelectedDays((prevSelectedDays) => prevSelectedDays.filter((d) => d !== day));
	};

	const handleClickDayDropdown = (event) => {
		setAnchorEl(anchorElement.current);
		setIsOpen((previousOpen) => !previousOpen);
	};


	return (
		<div className={styles['turno-container']} style={isItemFromList ? {marginBottom: '45px'} : {marginBottom: '0px', paddingLeft: isDeleteable ? 'initial' : '0px'}}>
			{isDeleteable && 
				<div className={styles['x-button']} onClick={() => {onDelete && onDelete(turno)}}>
					<EliminateSVG/>
				</div>
			}

			<div className={styles['name-hours-container']}>
				<div className={styles['name-container']}>
					<div className={styles['input-container']}>
						<input
							type="text"
							placeholder="Nombre turno..."
							className={styles['input']}
							value={name}
							onChange={(e) => setName(e.target.value)}
							disabled={!isEditable}
						/>
					</div>
				</div>

				<div className={styles['hours-container']}>
					<div className={styles['hour-container']}>
						<p>Desde</p>
						<TimeInput
							initValue={hourFrom}
							setHour={setHourFrom}
							isEnabled={isEditable}
						/>
					</div>

					<div className={styles['hour-container']}>
						<p>Hasta</p>
						<TimeInput initValue={hourTo} setHour={setHourTo} isEnabled={isEditable} />
					</div>
				</div>
			</div>

			<div className={styles['days-container']}>
				<p>Día/s que aplica</p>
				<div className={styles['days-bar']} ref={daysBarRef}>
					<div className={styles['svg-container']}>
						<CalendarSVG />
					</div>

					<div
						className={styles['days-window']}
						ref={containerRef}
						onClick={() => {
							if (isEditable) {
								setAnchorEl(anchorElement.current);
								setIsOpen(true);
							}
						}}
						style={{
							cursor: isEditable ? 'pointer' : 'default',
							color: !isEditable ? 'inherit' : 'inherit',
						}}>
						{selectedDays.length === 0 && (
							<p style={{ marginBottom: '0px', fontSize: '14px' }}>
								{isEditable ? 'Todos los dias' : turno.type === 'daily' ? "Todos los dias" : 'Sin dias seleccionados'}
							</p>
						)}
						{selectedDays.map((day) => (
							<div key={day} className={styles['day-pill']}>
								{day}
								{isEditable && (
									<span
										className={styles['remove-day']}
										onClick={(e) => {
											e.stopPropagation();
											handleDayRemove(day);
										}}>
										&times;
									</span>
								)}
							</div>
						))}
					</div>

					{isEditable && (
						<div
							className={styles['svg-container']}
							style={{
								transform: isOpen ? 'rotate(180deg)' : 'none',
								transition: 'transform 0.2s ease',
								cursor: 'pointer',
							}}
							onClick={isEditable ? handleClickDayDropdown : undefined}
							ref={anchorElement}>
							<ArrowSVG />
							{isOpen && (
								<DayPickerPopper
									isOpen={isOpen}
									setIsOpen={setIsOpen}
									anchorElement={anchorEl}
									placement="bottom-end"
									selectedDays={selectedDays}
									setSelectedDays={setSelectedDays}
									style={{
										marginTop: '12px',
										marginRight: '-15px',
										width: popperWidth,
									}}
									clickAwayHandler={() => {}}
								/>
							)}
						</div>
					)}
				</div>
			</div>

			<div className={styles['quantity-container']}>
				<p>Cant. Empleados</p>
				<div className={styles['input-container']}>
					<input
						type="number"
						placeholder="00"
						className={styles['input']}
						value={expected}
						onChange={(e) => {
							const value = parseInt(e.target.value);
							if (value >= 0) {
								setExpected(value);
							}
						}}
						disabled={!isEditable || colaboradoresID.length > 0}
					/>
				</div>
			</div>

			{!isEditable && (
				<div
					className={styles['colaborador-asignar-container']}
					style={{
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}>
					<EditSVG onClick={() => onDetailClick && onDetailClick(turno)} />
				</div>
			)}
		</div>
	);
};

export default QPTurno;
